import { HE_MONTHS } from '../utils/period';
import Icon from './Icon';

// A year of months as a 3×4 grid, plus year navigation. Deliberately free of
// react-day-picker and date-fns — twelve strings and integer arithmetic — which
// keeps it trivially unit-testable and keeps the ESM-transform problem confined
// to DayRangeView.
//
// Selection is a SINGLE month: clicking always applies exactly that calendar
// month. `span` is display-only, so a multi-month range arriving from a preset
// ("3 חודשים אחרונים") still shows which months are in scope without implying
// the grid can author a span.
//
// Layout is RTL by inheritance: the parent popover sets `direction: rtl`, so
// ינואר lands top-right on its own. Never place cells with explicit
// grid-column — that would fight the inherited direction and silently mirror
// the calendar back to LTR.
export default function MonthGrid({
  year,
  onYearChange,
  span = null,          // { startYm, endYm } — highlighted, not necessarily selectable
  todayYm = '',         // 'YYYY-MM' of the real current month, for the ring
  onPick,               // (ym: 'YYYY-MM') => void
  minYear,
  maxYear,
}) {
  const inSpan = (ym) => Boolean(span) && ym >= span.startYm && ym <= span.endYm;
  const isSingle = Boolean(span) && span.startYm === span.endYm;

  return (
    <div className="period-grid">
      <div className="period-grid-nav">
        {/* DOM order is [previous, label, next]. Under RTL that puts "previous"
            on the right, where the reading direction starts. The chevron glyph
            points left, so previous is the rotated one. */}
        <button
          type="button"
          className="period-grid-navbtn"
          onClick={() => onYearChange(year - 1)}
          disabled={minYear !== undefined && year - 1 < minYear}
          aria-label="שנה קודמת"
        >
          <Icon name="chevron-inline" size={16} className="period-grid-chev-prev" />
        </button>
        <span className="period-grid-year" aria-live="polite">{year}</span>
        <button
          type="button"
          className="period-grid-navbtn"
          onClick={() => onYearChange(year + 1)}
          disabled={maxYear !== undefined && year + 1 > maxYear}
          aria-label="שנה הבאה"
        >
          <Icon name="chevron-inline" size={16} />
        </button>
      </div>

      <div className="period-grid-cells" role="group" aria-label={`חודשי ${year}`}>
        {HE_MONTHS.map((name, i) => {
          const ym = `${year}-${String(i + 1).padStart(2, '0')}`;
          const selected = inSpan(ym);
          return (
            <button
              key={ym}
              type="button"
              className={
                'period-grid-cell'
                + (selected ? ' is-selected' : '')
                + (selected && !isSingle ? ' is-span' : '')
                + (ym === todayYm ? ' is-today' : '')
              }
              // aria-pressed rather than aria-selected: these are toggle-ish
              // buttons in a group, not options in a listbox.
              aria-pressed={selected}
              aria-label={`${name} ${year}`}
              onClick={() => onPick(ym)}
            >
              {name}
            </button>
          );
        })}
      </div>
    </div>
  );
}
